// src/utils/useEditorLink.js
//
// Returns a function that builds the correct 3D editor URL.
//
// Resolution order (first match wins):
//  1. Brand-specific editorDomain / editorUrl / editorOrigin (org accounts only)
//  2. Brand subdomainSlug → <slug>.app.devmydesignerplus.com
//  3. REACT_APP_EDITOR_URL env var (must be set correctly per environment)
//  4. Ultra-fallback: same origin — will log a warning so misconfiguration
//     is immediately visible in the console.
//
// ── IMPORTANT ────────────────────────────────────────────────────────────────
// REACT_APP_EDITOR_URL is a BUILD-TIME env var. It is baked into the JS
// bundle at `npm run build` time. If the deployed build shows the wrong URL,
// the .env file on the build server had the wrong value when the build ran.
//
// Environments:
//   local  → REACT_APP_EDITOR_URL=http://localhost:5173
//   dev    → REACT_APP_EDITOR_URL=https://app.devmydesignerplus.com
//   prod   → REACT_APP_EDITOR_URL=https://app.mydesignerplus.com
//
// Only ONE block should be uncommented in .env at any given time.
// ─────────────────────────────────────────────────────────────────────────────

import { useContext } from "react";
import { BrandContext } from "../context/BrandContext";

export function useEditorLink() {
  const brandCtx = useContext(BrandContext) || {};
  // BrandContext value is the raw brand object for org accounts, or null for
  // individual users. Be defensive about the shape.
  const brand = brandCtx?.brand || brandCtx?.value || brandCtx || {};

  // Build-time env var — must be set correctly for the target environment.
  const envEditorBase = (process.env.REACT_APP_EDITOR_URL || "").replace(/\/$/, "");

  // Brand-specific editor origin (org accounts with custom domains only)
  const brandEditorOrigin =
    brand?.editorDomain   // preferred — this is what the backend stores
    || brand?.editorOrigin
    || brand?.editorUrl
    || null;

  function getEditorOrigin() {
    // ── 1. Org with a configured custom editor domain ──────────────────
    if (brandEditorOrigin) {
      if (/^https?:\/\//i.test(brandEditorOrigin)) {
        return brandEditorOrigin.replace(/\/$/, "");
      }
      // bare domain → add protocol
      return `${window.location.protocol}//${brandEditorOrigin.replace(/\/$/, "")}`;
    }

    // ── 2. Org with subdomain slug (e.g. "acme" → acme.app.devmydesignerplus.com) ─
    if (brand?.subdomainSlug) {
      // Use REACT_APP_EDITOR_DOMAIN as the base for the subdomain if set,
      // otherwise fall back to deriving from REACT_APP_EDITOR_URL.
      const editorBaseDomain =
        process.env.REACT_APP_EDITOR_DOMAIN ||
        envEditorBase.replace(/^https?:\/\//, "");

      return `${window.location.protocol}//${brand.subdomainSlug}.${editorBaseDomain}`;
    }

    // ── 3. Env var (individual users and unbranded org accounts) ──────
    if (envEditorBase) return envEditorBase;

    // ── 4. Ultra fallback — should never reach here in production ─────
    // If this warning appears, REACT_APP_EDITOR_URL is missing from the .env
    // that was used when npm run build was executed.
    console.error(
      "[useEditorLink] REACT_APP_EDITOR_URL is not set. " +
      "Editor links will point to the dashboard origin, which is wrong. " +
      "Fix the .env file for this environment and rebuild."
    );
    return `${window.location.protocol}//${window.location.host}`;
  }

  /**
   * getEditorUrl(projectIdOrNew)
   *   "new"   → <editorOrigin>/editor/new
   *   "<id>"  → <editorOrigin>/editor/<id>
   */
  function getEditorUrl(projectIdOrNew) {
    const origin = getEditorOrigin();
    return projectIdOrNew === "new"
      ? `${origin}/editor/new`
      : `${origin}/editor/${projectIdOrNew}`;
  }

  return { getEditorUrl };
}